import React, { useContext } from 'react';
import { Button } from '@material-ui/core';
import PropTypes from 'prop-types';

// Icons
import EditIcon from '@material-ui/icons/Edit';

// Redux
import { connect } from 'react-redux';

// Constants
import { UserTypes } from '../constants';
import { T } from '../constants/tokens';
import { DrawerContext } from './DetailAside';

/**
 * The detail panel beside every list and the map.
 *
 * v7 keeps the header / body / footer structure but restyles it as a white
 * card with hairline rules. The status accent moves from a bar across the top
 * of the header to a thin rule under it, so the panel opens with the title
 * rather than with a block of color.
 */
function InfoPanel({
  children,
  statusColor,
  panelHeight,
  showEdit,
  editHref = null,
  header = null,
  footer = null,
  style = {}
}) {
  // Beside the content the panel is a card of a set height. In the drawer it
  // is the drawer: full height, so the footer sits at the very bottom and
  // the body gets everything above it. It used to keep the card's shorter
  // height there, which left a gap under the footer and made the body scroll
  // for want of room it actually had.
  const inDrawer = useContext(DrawerContext);
  const frame = inDrawer
    ? { flex: 1, minHeight: 0, border: 0, borderRadius: 0 }
    : { height: panelHeight, maxHeight: panelHeight, border: '1px solid ' + T.hairline, borderRadius: 6 };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: T.surfaceCard,
        overflow: 'hidden',
        ...frame,
        ...style
      }}
    >
      {header &&
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid ' + T.hairline }}>
          {header}
          {statusColor &&
            <div
              style={{
                height: 3,
                borderRadius: 9999,
                width: 40,
                marginTop: 12,
                background: statusColor
              }}
            />}
        </div>}

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
        {children}
      </div>

      {header && (footer || (editHref && showEdit)) &&
        <div style={{ padding: 16, borderTop: '1px solid ' + T.hairline }}>
          {footer}
          {editHref && showEdit &&
            <Button
              variant='outlined'
              href={editHref}
              fullWidth
              style={{ marginTop: footer ? 12 : 0 }}
            >
              Edit
              <EditIcon style={{ fontSize: 16, marginLeft: 8 }} />
            </Button>}
        </div>}
    </div>
  );
}

class InfoPanelWrapper extends React.Component {
  render() {
    return (
      <InfoPanel {...this.props} />
    );
  }
}

const mapStateToProps = (state) => ({
  // Window height less the 64px header and the content column's 32px gutters.
  // Was 132 when the shell had a second app bar under the first.
  panelHeight: state.ui.windowSize.height - 128,
  showEdit: (state.auth.userType === UserTypes.SUPER || state.auth.userType === UserTypes.IT) && state.setupMode.mode
});

export default connect(mapStateToProps, null)(InfoPanelWrapper);

InfoPanel.propTypes = {
  children: PropTypes.any,
  statusColor: PropTypes.string,
  panelHeight: PropTypes.number,
  showEdit: PropTypes.bool,
  editHref: PropTypes.string,
  header: PropTypes.any,
  footer: PropTypes.any,
  style: PropTypes.object
};
